import * as amplitude from '@amplitude/analytics-browser'
import router from '@/router'
import { titleCaseFromSnakeCase } from '@/utils/strings.js'

const state = {
  amplitudeState: 'init',
}

const getters = {
  trackingAllowed: (state) => {
    // if the app is in development mode, do not track
    // if the navigator object is not available something went wrong, assume the user cannot be tracked
    if (process.env.NODE_ENV === 'development' || !navigator) {
      return false
    }

    // navigator.globalPrivacyControl is new and only available in Firefox
    // true if the user explicitly does not provide consent to sell or share their data
    // false if the user either grants consent, or has not indicated a preference
    if (navigator.globalPrivacyControl !== undefined) {
      return !navigator.globalPrivacyControl
    }

    // This is being deprecated by web standards and is here only for compatibility with Chrome,
    // older Firefox, and Edge; this has already been removed by Safari.
    // The logic is:
    // - '1' if the user does not want to be tracked
    // - '0' if the user consents to being tracked
    // - null if the user has not indicated a preference
    if (navigator.doNotTrack === '1') {
      return false
    }
    return true
  },
}

const actions = {
  /**
   * Sends an Amplitude tracking event
   * @param {Object} context
   * @param {Object} params
   * @param {String} params.name - The name of the event
   * @param {Object} params.options - The options or "additional event properties" to pass to the event
   * @param {Object} params.overwrites - Overwrite [the event options](https://amplitude.github.io/Amplitude-TypeScript/interfaces/_amplitude_analytics_browser.Types.EventOptions.html)
   * @return {undefined}
   */
  async trackAmplitude(
    { commit, rootState, state, getters, rootGetters },
    { name, options, overwrites },
  ) {
    if (!getters.trackingAllowed || state.amplitudeState !== 'ready') {
      return
    }

    const opts = {}
    for (const key in options) {
      if (options[key] !== undefined) {
        opts[titleCaseFromSnakeCase(key)] = options[key]
      }
    }
    try {
      await amplitude.track(
        titleCaseFromSnakeCase(name),
        {
          ...opts,
          'Branding Enabled':
            rootState.branding?.brandedOnboardingEnabled || false,
          'Page Path': router.currentRoute.fullPath,
          'QR Type': rootState.branding?.entity,
          'SALUS User Agent': navigator.userAgent,
          source: rootGetters.qrCode ? 'qr' : 'default', // TODO: expand this to include other sources (potentially capture client?)
          'User ID': rootState.emailIdentifier || rootState.phoneIdentifier,
        },
        overwrites,
      )
    } catch (error) {
      commit('SET_AMPLITUDE_STATE', 'error')
    }
  },
  async flushEvents() {
    await amplitude.flush()
  },
  setAmplitudeState({ commit }, amplitudeState) {
    commit('SET_AMPLITUDE_STATE', amplitudeState)
  },
}

const mutations = {
  SET_AMPLITUDE_STATE(state, amplitudeState) {
    state.amplitudeState = amplitudeState
  },
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state,
}
